import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE } from '../utils/api';

interface CityContextType {
    selectedCity: string;
    setSelectedCity: (city: string) => void;
    cities: string[];
    allLocalities: any[];
    loadingCity: boolean;
}

const CityContext = createContext<CityContextType>({
    selectedCity: 'All Cities',
    setSelectedCity: () => {},
    cities: [],
    allLocalities: [],
    loadingCity: true,
});

export const useCity = () => useContext(CityContext);

export const CityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [selectedCity, setSelectedCity] = useState<string>('All Cities');
    const [cities, setCities] = useState<string[]>([]);
    const [allLocalities, setAllLocalities] = useState<any[]>([]);
    const [loadingCity, setLoadingCity] = useState<boolean>(true);

    useEffect(() => {
        // Fetch all localities with counts to extract unique cities
        fetch(`${API_BASE}/localities/with-counts`)
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setAllLocalities(data);
                    const uniqueCities = Array.from(new Set(data.map((loc: any) => loc.cityName?.trim()))).filter(Boolean) as string[];
                    setCities(uniqueCities);
                    
                    const fallbackCity = uniqueCities.length > 0 ? uniqueCities[0] : 'Gandhinagar';

                    // Use Geolocation API to find user's current city if not already set by user
                    if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(
                            async (position) => {
                                try {
                                    const { latitude, longitude } = position.coords;
                                    const res = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
                                    const geoData = await res.json();
                                    
                                    let detectedCity = geoData.city || geoData.locality || fallbackCity;
                                    const matchedCity = uniqueCities.find(c => c.trim().toLowerCase() === detectedCity.trim().toLowerCase());
                                    
                                    setSelectedCity(matchedCity || fallbackCity);
                                } catch (error) {
                                    console.error("Reverse geocoding error:", error);
                                    setSelectedCity('All Cities');
                                } finally {
                                    setLoadingCity(false);
                                }
                            },
                            () => {
                                // Geolocation denied or unavailable — silently fall back to 'All Cities'
                                setSelectedCity('All Cities');
                                setLoadingCity(false);
                            }
                        );
                    } else {
                        setSelectedCity('All Cities');
                        setLoadingCity(false);
                    }
                } else {
                    setSelectedCity('All Cities');
                    setLoadingCity(false);
                }
            })
            .catch(error => {
                console.error("Error fetching cities for context:", error);
                setSelectedCity('All Cities');
                setLoadingCity(false);
            });
    }, []);

    return (
        <CityContext.Provider value={{ selectedCity, setSelectedCity, cities, allLocalities, loadingCity }}>
            {children}
        </CityContext.Provider>
    );
};
